import type { RankOrderQuestion } from '../../../fetchSurvey/types/rankOrder';
import type { AnswerValue } from '../../../submitSurvey/types';
import type { LogicContext } from '../../types';
import { getVisibleRankOrderOptions } from '../../visibility/visibleItems';
import {
  getOptionRank,
  hasDuplicateRanks,
  normalizeRankOrderAnswers,
} from '../../fields/rankOrderUtils';

export function sanitizeRankOrderQuestionAnswer(
  question: RankOrderQuestion,
  answer: AnswerValue | undefined,
  context: LogicContext
): AnswerValue | undefined {
  const visibleOptions = getVisibleRankOrderOptions(question, context);
  const visibleOptionIds = new Set(visibleOptions.map(option => option.id));

  if (visibleOptionIds.size === 0) {
    return undefined;
  }

  const normalized = normalizeRankOrderAnswers(answer);
  const sanitized: Record<string, number> = {};

  for (const optionId of visibleOptionIds) {
    const rank = getOptionRank(normalized, optionId);
    if (rank !== undefined) {
      sanitized[optionId] = rank;
    }
  }

  if (Object.keys(sanitized).length === 0) {
    return undefined;
  }

  if (hasDuplicateRanks(sanitized, [...visibleOptionIds])) {
    const usedRanks = new Set<number>();
    const deduped: Record<string, number> = {};

    for (const option of visibleOptions) {
      const rank = getOptionRank(sanitized, option.id);
      if (rank === undefined || usedRanks.has(rank)) {
        continue;
      }
      usedRanks.add(rank);
      deduped[option.id] = rank;
    }

    return Object.keys(deduped).length > 0 ? deduped : undefined;
  }

  return sanitized;
}
